import { isEqual, sum, uniqBy } from 'lodash';
import { MIN_BASE_CELL_HEIGHT, MIN_BASE_CELL_WIDTH } from '@/layers/table-layer/default-configs';
import { rotatePointWithFocus } from '@/utils/math-kit';
import { Cell, ITableLayer } from '@/version-types';
import { CellInfo, CellsSelectedInfo } from '../state';

type Point = { x: number; y: number };
type SimpleCellInfo = { id: string; top: number; left: number; width: number; height: number };

/** 效果图: https://xiuxiu-pro-pre.meitudata.com/posters/63b0dc65be5cdcce341cf10365bdc155.png */
const getCellsByRange = ({
    allCells,
    range,
}: {
    allCells: SimpleCellInfo[];
    /** 相对于tableLayer内布局左上角的坐标 */
    range: { startPoint: Point; lastPoint: Point };
}): SimpleCellInfo[] | null => {
    const selectedCells = allCells.filter((cell) => {
        const cellStartX = cell.left;
        const cellStartY = cell.top;
        const cellEndX = cell.left + cell.width;
        const cellEndY = cell.top + cell.height;

        /** startPoint和lastPoint两个点组成的矩形 */
        const rectBounding = {
            top: Math.min(range.startPoint.y, range.lastPoint.y),
            left: Math.min(range.startPoint.x, range.lastPoint.x),
            right: Math.max(range.startPoint.x, range.lastPoint.x),
            bottom: Math.max(range.startPoint.y, range.lastPoint.y),
        };

        // Check if any part of the box intersects with the line
        if (
            cellStartX < rectBounding.right &&
            cellEndX > rectBounding.left &&
            cellStartY < rectBounding.bottom &&
            cellEndY > rectBounding.top
        ) {
            return true;
        }

        return false;
    });

    return selectedCells.length ? selectedCells : null;
};

type RectBounding = {
    top: number;
    left: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
};

/** 根据cells, 获取最左、最右、最上、最下的值 */
const getBoxesRectBounding = <T extends { top: number; left: number; width: number; height: number }>(
    boxes: T[],
): RectBounding => {
    const getBoundingItem = (boxes: T[], mode: 'top' | 'left' | 'right' | 'bottom') =>
        boxes.reduce((n, box) => {
            const { top, left, width, height } = box;
            const right = box.left + width;
            const bottom = box.top + height;
            // initial value
            if (isNaN(n)) {
                if (mode === 'top') return top;
                if (mode === 'bottom') return bottom;
                if (mode === 'left') return left;
                return right;
            }
            if (mode === 'top') return top < n ? top : n;
            if (mode === 'bottom') return bottom > n ? bottom : n;
            if (mode === 'left') return left < n ? left : n;
            return right > n ? right : n;
        }, NaN);
    const top = getBoundingItem(boxes, 'top');
    const left = getBoundingItem(boxes, 'left');
    const right = getBoundingItem(boxes, 'right');
    const bottom = getBoundingItem(boxes, 'bottom');
    const rectBounding: RectBounding = {
        top,
        left,
        right,
        bottom,
        width: right - left,
        height: bottom - top,
    };

    return rectBounding;
};

/**
 * 单元格多选逻辑
 * 将框选到的cells补充成一个矩形, 并返回最终框选的cells和矩形信息
 * 如补全这个效果图: https://xiuxiu-pro-pre.meitudata.com/posters/63b0dc65be5cdcce341cf10365bdc155.png
 * 结果为: https://xiuxiu-pro-pre.meitudata.com/posters/27b271b2da6d1f05c5439a2b02ba37cb.png
 * 如补全这个效果图: https://xiuxiu-pro-pre.meitudata.com/posters/17c86dd87ca72248e091762050a65fbb.png
 * 结果为: https://xiuxiu-pro-pre.meitudata.com/posters/330cf7329508f59d3ea6d80553e364f7.png
 */
export const getCellsSelectedInfoByMouseRange = ({
    tableLayer,
    range,
}: {
    tableLayer: ITableLayer;
    /** 鼠标滑动范围, 坐标: 相对于画布左上角 */
    range: Record<'startCoords' | 'lastCoords', { stageX: number; stageY: number }>;
}): CellsSelectedInfo | null => {
    /** 两个数值大致相等 */
    const isEqualNumber = (a: number, b: number) => Math.abs(a - b) < 0.001;

    const {
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
        cells: cellsProp,
    } = tableLayer.data;

    /** 相对于tableLayer内布局左上角的坐标 */
    const tableCenterPoint = {
        x: tableLayer.left + tableLayer.width / 2,
        y: tableLayer.top + tableLayer.height / 2,
    };

    /** 旋转前, 相对于tableLayer内布局左上角的坐标 */
    const fakeStartPoint = rotatePointWithFocus(
        {
            x: range.startCoords.stageX,
            y: range.startCoords.stageY,
        },
        tableCenterPoint,
        -tableLayer.rotate,
    );

    /** 旋转前, 相对于tableLayer内布局左上角的坐标 */
    const fakeEndPoint = rotatePointWithFocus(
        {
            x: range.lastCoords.stageX,
            y: range.lastCoords.stageY,
        },
        tableCenterPoint,
        -tableLayer.rotate,
    );

    /** 相对于tableLayer内布局左上角的坐标 */
    const startPoint = {
        x: fakeStartPoint.x - tableLayer.left,
        y: fakeStartPoint.y - tableLayer.top,
    };
    /** 相对于tableLayer内布局左上角的坐标 */
    const lastPoint = {
        x: fakeEndPoint.x - tableLayer.left,
        y: fakeEndPoint.y - tableLayer.top,
    };

    const cells = cellsProp.flat().filter(Boolean) as Cell[];
    const allCells: SimpleCellInfo[] = cells.map((cell) => ({
        id: cell.__renderId,
        text: cell.content,
        top: sum(verticalSizes.slice(0, cell.vIndex)),
        left: sum(horizontalSizes.slice(0, cell.hIndex)),
        width: sum(horizontalSizes.slice(cell.hIndex, cell.hIndex + cell.colSpan)),
        height: sum(verticalSizes.slice(cell.vIndex, cell.vIndex + cell.rowSpan)),
    }));

    const rectBounding = (function getRectBounding(_oldSelectedCells: SimpleCellInfo[] | null) {
        let oldSelectedCells: SimpleCellInfo[];
        if (_oldSelectedCells) {
            oldSelectedCells = _oldSelectedCells;
        } else {
            const initialSelectedCells = getCellsByRange({
                allCells,
                range: {
                    startPoint,
                    lastPoint,
                },
            });
            if (!initialSelectedCells) return null;
            oldSelectedCells = initialSelectedCells;
        }

        const rectBounding = getBoxesRectBounding(oldSelectedCells);
        const newSelectedCells = getCellsByRange({
            allCells,
            range: {
                // 给个0.001的偏差, 防止js精度问题导致计算出错
                startPoint: {
                    x: rectBounding.left + 0.001,
                    y: rectBounding.top + 0.001,
                },
                lastPoint: {
                    x: rectBounding.right - 0.001,
                    y: rectBounding.bottom - 0.001,
                },
            },
        });
        const isEqualRectSelectedCells = (aCells: SimpleCellInfo[], bCells: SimpleCellInfo[]) => {
            const aCellIds = aCells.map((cell) => cell.id).sort((a, b) => a.localeCompare(b));
            const bCellIds = bCells.map((cell) => cell.id).sort((a, b) => a.localeCompare(b));
            return isEqual(aCellIds, bCellIds);
        };
        if (newSelectedCells && isEqualRectSelectedCells(oldSelectedCells, newSelectedCells)) {
            return rectBounding;
        }
        return getRectBounding(newSelectedCells);
    })(null);

    if (!rectBounding) return null;

    const lefts = [
        ...horizontalSizes.map((_, index) => horizontalSizes.slice(0, index).reduce((a, b) => a + b, 0)),
        sum(horizontalSizes),
    ];
    const tops = [
        ...verticalSizes.map((_, index) => verticalSizes.slice(0, index).reduce((a, b) => a + b, 0)),
        sum(verticalSizes),
    ];

    const rect: CellsSelectedInfo['rect'] = {
        startHIndex: lefts.findIndex((left) => isEqualNumber(left, rectBounding.left)),
        startVIndex: tops.findIndex((top) => isEqualNumber(top, rectBounding.top)),
        endHIndex: lefts.findIndex((left) => isEqualNumber(left, rectBounding.left + rectBounding.width)),
        endVIndex: tops.findIndex((top) => isEqualNumber(top, rectBounding.top + rectBounding.height)),
    };

    const targetCellIds = allCells
        .filter((cell) => {
            const { top, left, width, height } = cell;
            const right = cell.left + width;
            const bottom = cell.top + height;
            return (
                top >= rectBounding.top &&
                left >= rectBounding.left &&
                right <= rectBounding.right &&
                bottom <= rectBounding.bottom
            );
        })
        .map((c) => c.id);

    return {
        rect,
        cellInfoList: cells
            .filter((c) => targetCellIds.includes(c.__renderId))
            .map((c) => ({
                id: c.__renderId,
                hIndex: c.hIndex,
                vIndex: c.vIndex,
            })),
    };
};

export const appendCellsSelectedInfoByPoint = ({
    tableLayer,
    originalSelectedRect,
    point,
}: {
    tableLayer: ITableLayer;
    originalSelectedRect: Record<'topLeft' | 'rightBottom', { x: number; y: number }>;
    point: { x: number; y: number };
}): CellsSelectedInfo | null => {
    const topLeft = { x: originalSelectedRect.topLeft.x, y: originalSelectedRect.topLeft.y };
    const topRight = { x: originalSelectedRect.rightBottom.x, y: originalSelectedRect.topLeft.y };
    const bottomLeft = { x: originalSelectedRect.topLeft.x, y: originalSelectedRect.rightBottom.y };
    const bottomRight = { x: originalSelectedRect.rightBottom.x, y: originalSelectedRect.rightBottom.y };
    const deltaX = MIN_BASE_CELL_WIDTH / 2;
    const deltaY = MIN_BASE_CELL_HEIGHT / 2;
    const cellInfoList: CellInfo[] = uniqBy(
        [
            ...(getCellsSelectedInfoByMouseRange({
                tableLayer,
                range: {
                    startCoords: { stageX: topLeft.x + deltaX, stageY: topLeft.y + deltaY },
                    lastCoords: { stageX: point.x, stageY: point.y },
                },
            })?.cellInfoList ?? []),
            ...(getCellsSelectedInfoByMouseRange({
                tableLayer,
                range: {
                    startCoords: { stageX: topRight.x - deltaX, stageY: topRight.y + deltaY },
                    lastCoords: { stageX: point.x, stageY: point.y },
                },
            })?.cellInfoList ?? []),
            ...(getCellsSelectedInfoByMouseRange({
                tableLayer,
                range: {
                    startCoords: { stageX: bottomLeft.x + deltaX, stageY: bottomLeft.y - deltaY },
                    lastCoords: { stageX: point.x, stageY: point.y },
                },
            })?.cellInfoList ?? []),
            ...(getCellsSelectedInfoByMouseRange({
                tableLayer,
                range: {
                    startCoords: { stageX: bottomRight.x - deltaX, stageY: bottomRight.y - deltaY },
                    lastCoords: { stageX: point.x, stageY: point.y },
                },
            })?.cellInfoList ?? []),
        ],
        'id',
    );
    const getDetailCellInfo = (cellInfo: CellInfo) => tableLayer.data.cells[cellInfo.vIndex]?.[cellInfo.hIndex] ?? null;
    const rect = {
        startHIndex: Math.min(...cellInfoList.map((cellInfo) => cellInfo.hIndex)),
        startVIndex: Math.min(...cellInfoList.map((cellInfo) => cellInfo.vIndex)),
        endHIndex: Math.max(
            ...cellInfoList.map((cellInfo) => {
                const detailCellInfo = getDetailCellInfo(cellInfo);
                if (!detailCellInfo) return 0;
                return detailCellInfo.hIndex + detailCellInfo.colSpan;
            }),
        ),
        endVIndex: Math.max(
            ...cellInfoList.map((cellInfo) => {
                const detailCellInfo = getDetailCellInfo(cellInfo);
                if (!detailCellInfo) return 0;
                return detailCellInfo.vIndex + detailCellInfo.rowSpan;
            }),
        ),
    };
    return {
        rect,
        cellInfoList,
    };
};
